import { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Collapse,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { IoChevronDown, IoChevronUp, IoClose } from 'react-icons/io5';
import { MdInfoOutline } from 'react-icons/md';
import Button from 'components/Common/Button';
import ConfirmModal from 'components/Common/ConfirmModal';
import { BusinessAddon } from '@/apis/business/addons/types';
import { useActivateBusinessAddon } from '@/apis/business/addons/hook';
import { formatDollars } from '@/utils/helperFunctions';
import { formatCommitmentFull, getAddonDisplayTitle, getDueTodayAmount, num } from './addonDisplay';

type TermsSection = { title: string; items: string[] };

const ADDON_TERMS: Partial<Record<BusinessAddon['code'], TermsSection[]>> = {
  sms_hosting: [
    {
      title: "What's Included",
      items: [
        'SMS hosting on your existing business number — voice service stays untouched.',
        'Agent-sent SMS follow-ups (booking links, directions, forms) during and after calls.'
      ]
    },
    {
      title: 'Good to Know',
      items: [
        'US bulk / marketing messaging — that requires the SMS for USA (A2P 10DLC) add-on.',
        'Carrier surcharges billed by your existing voice provider.'
      ]
    }
  ],
  a2p_10dlc: [
    {
      title: "What's Included",
      items: [
        'Brand and campaign registration required for U.S. application-to-person SMS on 10DLC numbers.',
        'Link approved campaigns to hosted or carrier numbers so messages can reach U.S. carriers.'
      ]
    },
    {
      title: 'Good to Know',
      items: [
        'Vetting and campaign fees may apply per submission.',
        'Carrier pass-through fees are billed at cost as configured by your agency.'
      ]
    }
  ]
};

interface ActivateAddonDrawerProps {
  addon: BusinessAddon | null;
  open: boolean;
  onClose: () => void;
}

function DueTodayBadge() {
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.25,
        borderRadius: 10,
        bgcolor: '#F3F4F6',
        color: 'text.secondary',
        fontSize: 11,
        fontWeight: 500,
        ml: 1
      }}
    >
      Due Today
    </Box>
  );
}

function ChargeRow({
  label,
  amount,
  helper,
  badge,
  borderBottom = true
}: {
  label: string;
  amount: string;
  helper?: string;
  badge?: boolean;
  borderBottom?: boolean;
}) {
  return (
    <Box
      px={2}
      py={1.75}
      borderBottom={borderBottom ? '1px solid #ECECEC' : 'none'}
      display='flex'
      justifyContent='space-between'
      alignItems='flex-start'
      gap={2}
    >
      <Box>
        <Typography fontSize={14} fontWeight={500} display='flex' alignItems='center' flexWrap='wrap'>
          {label}
          {badge && <DueTodayBadge />}
        </Typography>
        {helper && (
          <Typography fontSize={12} color='text.secondary' mt={0.35}>
            {helper}
          </Typography>
        )}
      </Box>
      <Typography fontSize={14} fontWeight={600} whiteSpace='nowrap'>
        {amount}
      </Typography>
    </Box>
  );
}

function InfoNote({ children }: { children: React.ReactNode }) {
  const theme = useTheme();
  return (
    <Stack direction='row' gap={1} alignItems='flex-start' mt={2}>
      <MdInfoOutline size={16} color={theme.palette.text.secondary} style={{ marginTop: 2, flexShrink: 0 }} />
      <Typography fontSize={12} color='text.secondary' lineHeight={1.5}>
        {children}
      </Typography>
    </Stack>
  );
}

function TermsAndOfferings({ addon }: { addon: BusinessAddon }) {
  const [open, setOpen] = useState(false);
  const sections = ADDON_TERMS[addon.code];
  const description = addon.description?.trim() || '';

  return (
    <Box mt={2.5}>
      <Box
        component='button'
        type='button'
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          border: 0,
          background: 'none',
          cursor: 'pointer',
          p: 0,
          color: '#6B7280',
          '&:hover': { color: '#374151' }
        }}
      >
        {open ? <IoChevronUp size={16} /> : <IoChevronDown size={16} />}
        <Typography
          component='span'
          fontSize={12}
          fontWeight={700}
          letterSpacing={0.5}
          textTransform='uppercase'
          color='inherit'
        >
          Terms and Offerings
        </Typography>
      </Box>

      <Collapse in={open} timeout='auto' unmountOnExit>
        <Box mt={1.5}>
          {sections ? (
            sections.map((section) => (
              <Box key={section.title} mb={2}>
                <Typography fontSize={14} fontWeight={600} color='#111827' mb={0.75}>
                  {section.title}
                </Typography>
                <Box component='ul' sx={{ m: 0, pl: 2.25, color: '#6B7280' }}>
                  {section.items.map((item) => (
                    <Typography
                      key={item}
                      component='li'
                      fontSize={13}
                      color='inherit'
                      lineHeight={1.55}
                      sx={{ mb: 0.75 }}
                    >
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))
          ) : description ? (
            <Typography fontSize={13} color='#6B7280' lineHeight={1.55} whiteSpace='pre-wrap'>
              {description}
            </Typography>
          ) : (
            <Typography fontSize={13} color='#6B7280'>
              No additional terms provided by your agency.
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function PrioritySupportCharges({ addon }: { addon: BusinessAddon }) {
  const p = addon.prices || {};
  const charge = addon.activation_charge;
  const setup = charge ? num(charge.one_time_fee) : num(p.one_time_setup_fee);
  const perLocation = num(p.price_per_location);
  const minLocations = Math.max(num(p.minimum_locations_billed), 1);
  const billableLocations = Math.max(num(charge?.billable_quantity), minLocations);
  const recurringDue = charge ? num(charge.recurring_due) : perLocation * billableLocations;
  const dueToday = getDueTodayAmount(addon);
  const isProrated = num(charge?.prorate_ratio) > 0 && num(charge?.prorate_ratio) < 1;

  return (
    <>
      <Typography fontSize={14} mb={1.5}>
        The following will be charged to your payment method on file:
      </Typography>
      <Box border='1px solid #ECECEC' borderRadius={2} overflow='hidden'>
        {setup > 0 && (
          <ChargeRow label='One-Time Setup Fee' amount={formatDollars(setup)} helper='Charged once activated' badge />
        )}
        <ChargeRow
          label='Priority Support'
          amount={`${formatDollars(perLocation)}/location/month`}
          helper={
            isProrated
              ? `${billableLocations} location${billableLocations === 1 ? '' : 's'} · ${formatDollars(recurringDue)} on your next monthly invoice (prorated for ${charge?.remaining_days}/${charge?.days_in_month} days)`
              : `Minimum ${minLocations} location · billing ${billableLocations} · billed with your monthly subscription`
          }
          borderBottom={false}
        />
        <Box bgcolor='#F9FAFB' px={2} py={1.75} display='flex' justifyContent='space-between' alignItems='flex-start'>
          <Typography fontWeight={600}>Due Today</Typography>
          <Box textAlign='right'>
            <Typography fontWeight={600}>{formatDollars(dueToday)}</Typography>
            <Typography fontSize={12} color='text.secondary'>
              {formatCommitmentFull(addon.commitment_months)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <InfoNote>
        {isProrated
          ? 'Only the setup fee is charged today. Location fees for the current cycle are prorated and billed with your monthly subscription, then billed in full each month after.'
          : 'Only the setup fee is charged today. Location fees are billed in full with your monthly subscription when activated on the 1st.'}
      </InfoNote>
    </>
  );
}

function SmsHostingCharges({ addon }: { addon: BusinessAddon }) {
  const p = addon.prices || {};
  const setup = num(p.one_time_setup_fee);
  const monthly = num(p.price_per_hosted_number);
  const dueToday = getDueTodayAmount(addon);

  return (
    <>
      <Typography fontSize={14} mb={1.5}>
        The following will be charged to your payment method on file:
      </Typography>
      <Box border='1px solid #ECECEC' borderRadius={2} overflow='hidden'>
        <ChargeRow
          label='One-Time Setup Fee'
          amount={formatDollars(setup)}
          helper={setup > 0 ? 'Charged once activated' : 'Waived by your agency'}
          badge={setup > 0}
        />
        <ChargeRow
          label='SMS Hosting'
          amount={`${formatDollars(monthly)}/hosted number/month`}
          helper='Charged for every number you host'
          borderBottom={false}
        />
        <Box bgcolor='#F9FAFB' px={2} py={1.75} display='flex' justifyContent='space-between' alignItems='flex-start'>
          <Typography fontWeight={600}>Due Today</Typography>
          <Box textAlign='right'>
            <Typography fontWeight={600}>{formatDollars(dueToday)}</Typography>
            <Typography fontSize={12} color='text.secondary'>
              {formatCommitmentFull(addon.commitment_months)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <InfoNote>
        Each approved hosted number adds the full monthly fee. Standard carrier messaging rates from your provider may
        still apply. Billed monthly for every hosted number.
      </InfoNote>
    </>
  );
}

function A2pCharges({ addon }: { addon: BusinessAddon }) {
  const p = addon.prices || {};
  const setup = num(p.one_time_setup_fee);
  const vettingFee = num(p.vetting_fee_per_campaign);
  const lowVolume = num(p.low_volume_mixed_campaign);
  const mixed = num(p.mixed_campaign_per_month);
  const maxNumbers = num(p.max_numbers_per_campaign) || 10;
  const dueToday = getDueTodayAmount(addon);

  return (
    <>
      <Typography fontSize={14} fontWeight={500} mb={1.5}>
        The following will be charged to your payment method on file:
      </Typography>
      <Box border='1px solid #ECECEC' borderRadius={2} overflow='hidden'>
        <ChargeRow label='One-Time Setup Fee' amount={formatDollars(setup, 9)} helper='Charged once activated' badge />
        <ChargeRow
          label='Vetting Fee'
          amount={formatDollars(vettingFee, 9)}
          helper='Charged for each campaign submission and resubmission.'
          borderBottom={false}
        />
        <Box bgcolor='#F9FAFB' px={2} py={1.75} display='flex' justifyContent='space-between' alignItems='flex-start'>
          <Typography fontWeight={600}>Due Today</Typography>
          <Box textAlign='right'>
            <Typography fontWeight={600}>{formatDollars(dueToday)}</Typography>
            <Typography fontSize={12} color='text.secondary'>
              {formatCommitmentFull(addon.commitment_months)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <InfoNote>
        Carrier pass-through fees are billed at cost. A minimum commitment term applies as configured by your agency.
        Charges are prorated for the current billing cycle, then billed monthly for each campaign.
      </InfoNote>

      <Typography fontSize={14} fontWeight={600} textTransform='uppercase' mt={3} mb={1.5}>
        Businesses can choose from the campaign types below based on their needs
      </Typography>
      <Box border='1px solid #ECECEC' borderRadius={2} overflow='hidden'>
        <ChargeRow
          label='Low-Volume Mixed Campaign'
          amount={`${formatDollars(lowVolume, 9)}/campaign/month`}
          helper='Conversational messaging appointment, reminder and reply traffic.'
        />
        <ChargeRow
          label='Mixed Campaign'
          amount={`${formatDollars(mixed, 9)}/campaign/month`}
          helper='Marketing and bulk messaging with higher carrier throughput.'
          borderBottom={false}
        />
      </Box>
      <InfoNote>
        You are billed for the selected campaign only — not both. The campaign covers all numbers registered under it
        (up to {maxNumbers}).
      </InfoNote>
    </>
  );
}

function AiWorkflowCharges({ addon }: { addon: BusinessAddon }) {
  const p = addon.prices || {};
  const charge = addon.activation_charge;
  const setup = charge ? num(charge.one_time_fee) : num(p.one_time_setup_fee);
  const monthly = num(p.monthly_price_all_locations);
  const includedFull = num(charge?.included_runs_full ?? p.included_runs_per_month);
  const includedDue = num(charge?.included_runs_due ?? includedFull);
  const perRun = num(p.price_per_additional_run);
  const recurringDue = charge ? num(charge.recurring_due) : monthly;
  const dueToday = getDueTodayAmount(addon);
  const isProrated = num(charge?.prorate_ratio) > 0 && num(charge?.prorate_ratio) < 1;

  return (
    <>
      <Typography fontSize={14} mb={1.5}>
        The following will be charged to your payment method on file:
      </Typography>
      <Box border='1px solid #ECECEC' borderRadius={2} overflow='hidden'>
        <ChargeRow
          label='One-Time Setup Fee'
          amount={formatDollars(setup)}
          helper={setup > 0 ? 'Charged once activated' : 'Waived by your agency'}
          badge={setup > 0}
        />
        <ChargeRow
          label='Workflow (AI Admin)'
          amount={`${formatDollars(monthly)}/month`}
          helper={
            isProrated
              ? `${formatDollars(recurringDue)} on your next monthly invoice (prorated for ${charge?.remaining_days}/${charge?.days_in_month} days) · includes ${includedDue.toLocaleString()} runs this cycle (from ${includedFull.toLocaleString()})`
              : `Includes ${includedFull.toLocaleString()} runs / month across all locations · billed with your monthly subscription`
          }
        />
        <ChargeRow
          label='Additional Runs'
          amount={`${formatDollars(perRun)}/run`}
          helper={`Overage after the included ${includedDue.toLocaleString()} runs this cycle · billed with your monthly subscription`}
          borderBottom={false}
        />
        <Box bgcolor='#F9FAFB' px={2} py={1.75} display='flex' justifyContent='space-between' alignItems='flex-start'>
          <Typography fontWeight={600}>Due Today</Typography>
          <Box textAlign='right'>
            <Typography fontWeight={600}>{formatDollars(dueToday)}</Typography>
            <Typography fontSize={12} color='text.secondary'>
              {formatCommitmentFull(addon.commitment_months)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <InfoNote>
        Only the setup fee is charged today. The monthly fee (prorated in the activation month), included runs, and any
        overage are billed with your monthly subscription. Usage can be monitored under Settings → Usage.
      </InfoNote>
    </>
  );
}

export default function ActivateAddonDrawer({ addon, open, onClose }: ActivateAddonDrawerProps) {
  const [agreed, setAgreed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutateAsync: activateAddon, isPending } = useActivateBusinessAddon();

  useEffect(() => {
    if (!open) return;
    setAgreed(false);
    setConfirmOpen(false);
  }, [open, addon?.id]);

  if (!addon) return null;

  const title = `Activate ${getAddonDisplayTitle(addon)}`;
  const dueToday = getDueTodayAmount(addon);

  const onConfirmActivate = async () => {
    try {
      await activateAddon(addon.code);
      setConfirmOpen(false);
      onClose();
    } catch {
      setConfirmOpen(false);
    }
  };

  return (
    <>
      <Drawer
        anchor='right'
        open={open}
        onClose={() => {
          if (!isPending) onClose();
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 520,
            maxWidth: '100%',
            p: 0,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          px={3}
          py={2}
          borderBottom='1px solid #ECECEC'
        >
          <Typography variant='h6' fontWeight={600}>
            {title}
          </Typography>
          <IconButton onClick={onClose} size='small' aria-label='Close' disabled={isPending}>
            <IoClose size={22} />
          </IconButton>
        </Box>

        <Box flex={1} overflow='auto' px={3} py={2.5}>
          {addon.code === 'priority_support' && <PrioritySupportCharges addon={addon} />}
          {addon.code === 'sms_hosting' && <SmsHostingCharges addon={addon} />}
          {addon.code === 'a2p_10dlc' && <A2pCharges addon={addon} />}
          {addon.code === 'ai_workflow' && <AiWorkflowCharges addon={addon} />}

          <TermsAndOfferings key={addon.id} addon={addon} />

          <FormControlLabel
            sx={{ mt: 3, alignItems: 'flex-start', ml: 0 }}
            control={
              <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} size='small' sx={{ pt: 0.25 }} />
            }
            label={
              <Typography fontSize={13}>
                I understand the charges and terms above and agree to activate this add-on.
              </Typography>
            }
          />
        </Box>

        <Stack direction='row' justifyContent='flex-end' gap={1.5} px={3} py={2} borderTop='1px solid #ECECEC'>
          <Button variant='outlined' color='inherit' onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant='contained' onClick={() => setConfirmOpen(true)} disabled={!agreed || isPending}>
            Confirm & Activate
          </Button>
        </Stack>
      </Drawer>

      <ConfirmModal
        open={confirmOpen}
        onClose={() => {
          if (!isPending) setConfirmOpen(false);
        }}
        onConfirm={() => {
          if (isPending) return;
          void onConfirmActivate();
        }}
        title='Confirm Payment'
        description={
          <>
            Are you sure you want to pay <strong>{formatDollars(dueToday)}</strong> and activate this Add-On?
          </>
        }
        cancelText='Cancel'
        confirmText='Yes Sure'
        isPending={isPending}
        loadingText='Activating'
      />
    </>
  );
}
